import { useMemo } from 'react';
import { uniq } from 'lodash-es';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { LegLocationTypeEnum, RouteLegTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { useWatchFields } from '@/hooks/useWatchFields';
import { SelectItemType } from '@/types';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { EUROPE_REGION_ID } from '@/constants/serviceRegion.constants';
import { LegTimeLink } from './LegTimeLink';
import { DepartureLocation } from './DepartureLocation';
import { ArrivalLocation } from './ArrivalLocation';

export const MainLocationsInputs = () => {
  const { serviceRegionId } = useBaseCaseDetails();
  const { countriesByIso2 } = useCountriesData();
  const { getLocationById } = useRouteLocations();
  const { isMainDataEdit, initialSegment, currentLeg, getSegmentLocations } = useLegFormContext();

  const { segmentType, legType } = useWatchFields<CommonRouteLegSchemaValues>(['segmentType', 'legType'] as const);

  // DH/TXC already used by the leg (e.g. not the case ones) have to stay selectable
  const legFacilityLocationIds = useMemo(() => {
    if (!currentLeg || !('departureLocation' in currentLeg)) return [];

    return [currentLeg.departureLocation, currentLeg.arrivalLocation]
      .filter((l) => !!l && [LegLocationTypeEnum.hospital, LegLocationTypeEnum.txcenter].includes(l.locationType))
      .map((l) => l.locationId);
  }, [currentLeg]);

  const predefinedLocations = useMemo(() => {
    return getSegmentLocations(
      initialSegment?.type || '',
      segmentType === SegmentTypeEnum.custom,
      legFacilityLocationIds,
    );
  }, [initialSegment?.type, getSegmentLocations, segmentType, legFacilityLocationIds]);

  const countries = useMemo<SelectItemType[]>(() => {
    if (serviceRegionId === EUROPE_REGION_ID) {
      return Object.values(countriesByIso2)
        .filter((country) => country.serviceRegionId === EUROPE_REGION_ID)
        .map((country) => ({
          value: country.alpha2,
          name: country.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) as SelectItemType[];
    }

    return uniq(predefinedLocations.map((l) => getLocationById(l.id)?.location.country))
      .filter((countryCode): countryCode is string => Boolean(countryCode))
      .map((countryCode) => ({
        value: countryCode,
        name: countriesByIso2[countryCode]?.name ?? countryCode,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)) as SelectItemType[];
  }, [countriesByIso2, getLocationById, predefinedLocations, serviceRegionId]);

  const showTimeLink = [
    RouteLegTypeEnum.TmdxJetAviaLeg,
    RouteLegTypeEnum.TxCenterProviderAviaLeg,
    RouteLegTypeEnum.SupplementalLiftAviaLeg,
    RouteLegTypeEnum.TmdxAffiliateGroundLeg,
    RouteLegTypeEnum.TxCenterProviderGroundLeg,
    RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    RouteLegTypeEnum.TmdxDriveTedsGroundLeg,
    RouteLegTypeEnum.SelfServicedGroundLeg,
  ].includes(legType);

  return (
    <div className="relative flex flex-col gap-6">
      {showTimeLink && <LegTimeLink isDisable={!isMainDataEdit} className="absolute right-[12.7rem] mt-5 z-auto" />}

      <DepartureLocation predefinedLocations={predefinedLocations} countriesList={countries} />
      <ArrivalLocation predefinedLocations={predefinedLocations} countriesList={countries} />
    </div>
  );
};
